type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

export enum CacheMode {
    Default = "default",
    NoStore = "no-store",
    Reload = "reload",
    NoCache = "no-cache",
    ForceCache = "force-cache",
    OnlyIfCached = "only-if-cached",
}

export async function request(
    method: HttpMethod,
    url: string,
    body?: any,
    cache: CacheMode = CacheMode.Default,
    timeoutMs?: number,
) {
    const options: RequestInit = {
        method,
        mode: "cors",
        cache: cache,
        headers: new Headers()
    };

    // Add Authorization header if access_token is available
    const accessToken = localStorage.getItem("access_token");
    if (accessToken && options.headers instanceof Headers) {
        options.headers.set("Authorization", `Bearer ${accessToken}`);
    }

    if (body) {
        // Handle form data
        if (body instanceof FormData) {
            options.body = body;
        } else {
            if (options.headers instanceof Headers) {
                options.headers.set("Content-Type", "application/json");
            }

            // If textForm is true, use the body as-is; otherwise, stringify it
            options.body = JSON.stringify(body);
        }
    }
    if (!timeoutMs) return await fetch(url, options);

    // Give up instead of letting a stuck server leave the UI waiting forever.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await fetch(url, { ...options, signal: controller.signal });
    } finally {
        clearTimeout(timer);
    }
}

export const fetchWrapperPublic = {
    async get(url: string, cache?: CacheMode) {
        return await request("GET", url, undefined, cache);
    },
    async post(url: string, body?: any) {
        return await request("POST", url, body);
    },
    async publicPost(url: string, body: any, timeoutMs?: number) {
        return await request("POST", url, body, undefined, timeoutMs);
    },
    async put(url: string, body?: any) {
        return await request("PUT", url, body);
    },
    async delete(url: string, body?: any) {
        return await request("DELETE", url, body);
    },
    async patch(url: string, body?: any) {
        return await request("PATCH", url, body);
    },
};

export interface ApiResponse<T> {
    body: T;
    message?: string;
    status: number;
    error?: string;
}